import { useState, useEffect } from 'react';

// MUI Imports
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import { LOST_REASONS } from '../../../schema/lostReasons.js';

// Asked before a quote is marked Lost. Confirm hands back { category, text, offerId }; cancelling
// leaves the quote as it was. With several offers it also asks which one was lost, so reports count
// that offer instead of the first; "Not sure" leaves offerId out.
function LostReasonDialog(props) {
    const { open, onClose, onConfirm, working, offers = [] } = props;
    const [ category, setCategory ] = useState(null);
    const [ text, setText ] = useState('');
    const [ offerId, setOfferId ] = useState('');

    useEffect(() => {
        if (open) {
            setCategory(null);
            setText('');
            setOfferId('');
        }
    }, [ open ]);

    const needsText = category === 'other';
    const canConfirm = !!category && (!needsText || text.trim().length > 0) && !working;

    return (
        <Dialog open={open} onClose={working ? undefined : onClose} maxWidth='xs' fullWidth onClick={e => e.stopPropagation()}>
            <DialogTitle sx={{ fontSize: '16px', fontWeight: 700 }}>Why was this quote lost?</DialogTitle>
            <DialogContent>
                <Typography variant='body2' sx={{ color: 'text.secondary', marginBottom: '8px' }}>
                    Pick the main reason. It is saved in the quote's activity and counted in Quotations Analytics.
                </Typography>
                <RadioGroup value={category || ''} onChange={e => setCategory(e.target.value)}>
                    {LOST_REASONS.map(r => (
                        <FormControlLabel
                            key={r.key}
                            value={r.key}
                            control={<Radio size='small' />}
                            label={<Typography variant='body2'>{r.label}</Typography>}
                        />
                    ))}
                </RadioGroup>
                {needsText && (
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={2}
                        size='small'
                        placeholder='Tell us what happened'
                        value={text}
                        onChange={e => setText(e.target.value)}
                        inputProps={{ maxLength: 1000 }}
                        sx={{ marginTop: '8px' }}
                    />
                )}
                {offers.length > 1 && (
                    <>
                        <Typography variant='body2' sx={{ fontWeight: 600, marginTop: '16px', marginBottom: '4px' }}>
                            Which offer was lost?
                        </Typography>
                        <RadioGroup value={offerId} onChange={e => setOfferId(e.target.value)}>
                            {offers.map((offer, i) => (
                                <FormControlLabel
                                    key={offer.id}
                                    value={offer.id}
                                    control={<Radio size='small' />}
                                    label={<Typography variant='body2'>Offer {i + 1}{offer.title ? `: ${offer.title}` : ''}</Typography>}
                                />
                            ))}
                            <FormControlLabel
                                value=''
                                control={<Radio size='small' />}
                                label={<Typography variant='body2'>Not sure (reports use offer 1)</Typography>}
                            />
                        </RadioGroup>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ padding: '0 24px 20px' }}>
                <Button onClick={onClose} disabled={working} variant='outlined' sx={{ textTransform: 'none' }}>Cancel</Button>
                <Button
                    onClick={() => onConfirm({ category, ...(needsText ? { text: text.trim() } : {}), ...(offerId ? { offerId } : {}) })}
                    disabled={!canConfirm}
                    variant='contained'
                    color='error'
                    sx={{ textTransform: 'none' }}
                    startIcon={working ? <CircularProgress size={14} color='inherit' /> : null}
                >
                    Mark as Lost
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default LostReasonDialog;
